import {Component, inject, ViewChild} from '@angular/core';
import {AutoUnsubscribe} from 'src/infrastructure/decorators/auto-unsubscribe.decorator';
import {MatDialog} from '@angular/material/dialog';
import {DEFAULT_CLIENT_PAY_STATUS, DEFAULT_SORT_PROPERTY_NAME} from '../../constants/client-balance-filter';
import {GridEventOutput, GridModel} from 'src/app/shared/model/page-grid';
import {ClientBalanceGridService} from '../../service/client-balance-grid.service';
import {ClientBalanceDataResponse, ClientBalanceResponse} from '../../models/client-balance';
import {TramsDataSourceService} from 'src/app/payments/service/trams-data-source.service';
import {TramsDataSourceModel} from 'src/app/payments/models/trams-data-source';
import {AdxBookingService} from 'src/app/adx-booking/service/adx-booking.service';
import {PayStatusesResponse} from 'src/app/adx-booking/model/pay-statuses-response';
import {DatePipe} from '@angular/common';
import {PageGridComponent} from 'src/app/shared/components/page-grid/page-grid.component';
import {ClientStatementComponent} from 'src/app/statements/components/client-statement/client-statement.component';

@Component({
    selector: 'app-client-balance',
    templateUrl: './client-balance.component.html',
    standalone: false
})

@AutoUnsubscribe()
export class ClientBalanceComponent {

  @ViewChild(PageGridComponent) protected clientBalanceGridRef?: PageGridComponent;

  private readonly dialog = inject(MatDialog);
  private dataSourceService = inject(TramsDataSourceService);
  private adxBookingService = inject(AdxBookingService);
  private datePipe = inject(DatePipe);

  protected readonly clientBalanceGrid: GridModel<ClientBalanceResponse> = {
    gridDataTestId: 'client-balance-list',
    gridTitle: 'Client Balance',
    gridTitleFunction: (filter: {asOfDate: Date})=> `Client Balance As Of ${this.datePipe.transform(filter.asOfDate, 'dd MMM y') || ''}`,
    replayButton: true,
    pdfButton: true,
    defaultSort: { active: DEFAULT_SORT_PROPERTY_NAME, direction: 'desc' },
    columns: [
      {
        columnProperty: 'clientName',
        columnHeader: 'Client Name',
        sort: true,
        clickable: true,
        clickableWithItem: (item: ClientBalanceDataResponse) => item.hasStatements && !!this.clientBalanceGridRef?.filters?.['asOfDate'],
        textLeft: true,
        linkEventFunction: (item:ClientBalanceDataResponse, filter:{asOfDate: Date})=> {
          const asOfDateParam = filter.asOfDate ? `&AsOfDate=${this.datePipe.transform(filter.asOfDate, 'dd MMM y')}` : '';
          return `Clients/${item?.id}/Statements?ClientName=${item?.clientName}${asOfDateParam}`;
        },
      },
      {
        columnProperty: 'id',
        columnHeader: 'Client Number',
        sort: true,
      },
      {
        columnProperty: 'email',
        columnHeader: 'Client Email',
        sort: true,
        textLeft: true,
      },
      {
        columnProperty: 'phoneNumber',
        columnHeader: 'Phone Number',
        sort: true,
      },
      {
        columnProperty: 'totalBilled',
        columnHeader: 'Invoice Total',
        sort: true,
      },
      {
        columnProperty: 'outstandingBalance',
        columnHeader: 'Balance Due',
        sort: true,
      },
      {
        columnProperty: 'dataSourceName',
        columnHeader: 'Data Source',
        sort: true,
        textLeft: true,
      },
    ],
    filters: [
      {
        filterProperty: 'asOfDate',
        filterDisplayName: 'As Of Date',
        filterType: 'DATE',
        filterResetValue: new Date(),
      },
      {
        filterProperty: 'clientPayStatusId',
        filterDisplayName: 'Client Pay Status',
        filterType: 'DROPDOWN',
        config: {
          dropdownOptions: this.adxBookingService.getPayStatuses({isDeleted: false}),
          dropdownValueProp: 'id',
          dropdownDisplayWith: (item: PayStatusesResponse) => item.payStatusName
        }
      },
      {
        filterProperty: 'paymentDateFrom',
        filterDisplayName: 'Payment Date From',
        filterType: 'DATE',
      },
      {
        filterProperty: 'paymentDateTo',
        filterDisplayName: 'Payment Date To',
        filterType: 'DATE',
      },
      {
        filterProperty: 'clientName',
        filterDisplayName: 'Client Name',
        filterType: 'AUTOCOMPLETE',
        config: {
          autocompleteTableName: 'ClientBalancesView',
          autocompleteColumnName: 'ClientName',
          autocompleteEmitText: true,
        }
      },
      {
        filterProperty: 'id',
        filterDisplayName: 'Client Number',
        filterType: 'AUTOCOMPLETE',
        config: {
          autocompleteTableName: 'ClientBalancesView',
          autocompleteColumnName: 'Id',
          autocompleteEmitText: true,
        }
      },
      {
        filterProperty: 'minOutstandingBalance',
        filterDisplayName: 'Balance Due Min',
        filterType: 'NUMBER',
      },
      {
        filterProperty: 'maxOutstandingBalance',
        filterDisplayName: 'Balance Due Max',
        filterType: 'NUMBER',
      },
      {
        filterProperty: 'minTotalBilled',
        filterDisplayName: 'Invoice Total Min',
        filterType: 'NUMBER',
      },
      {
        filterProperty: 'maxTotalBilled',
        filterDisplayName: 'Invoice Total Max',
        filterType: 'NUMBER',
      },
      {
        filterProperty: 'dataSourceId',
        filterDisplayName: 'Data Source',
        filterType: 'DROPDOWN',
        config: {
          dropdownOptions: this.dataSourceService.getTramsDataSourceList(),
          dropdownValueProp: 'id',
          dropdownDisplayWith: (item: TramsDataSourceModel) => item.sourceName
        }
      },
    ],
    filtersInitial: {
      asOfDate: new Date(),
      clientPayStatusId: DEFAULT_CLIENT_PAY_STATUS,
    },
    gridGetService: inject(ClientBalanceGridService)
  };

  protected handleItemClick({column, item}: GridEventOutput<ClientBalanceDataResponse>): void {
    if(column === 'clientName') {
      this.openDialog(item, 'Client Statement');
    }
  }

  private openDialog(record: ClientBalanceDataResponse, titleHeader: string): void {
    const asOfDate = this.clientBalanceGridRef?.filters?.['asOfDate'];
    if (!asOfDate) {
      return;
    }
    this.dialog.open(ClientStatementComponent, {
      width: '55%',
      data: {
        title: titleHeader,
        clientId: record.id,
        clientName: record.clientName,
        asOfDate: this.datePipe.transform(asOfDate, 'dd MMM y') ?? '',
      },
    });
  }
}
